import { createContext, useContext, useState, useMemo, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeContextProvider");
  }
  return context;
};

// shadcn-inspired color palette
const lightPalette = {
  primary: {
    main: "#000000",
    light: "#404040",
    dark: "#000000",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#71717a",
    light: "#a1a1aa",
    dark: "#52525b",
    contrastText: "#ffffff",
  },
  background: {
    default: "#ffffff",
    paper: "#ffffff",
  },
  text: {
    primary: "#000000",
    secondary: "#71717a",
    disabled: "#d4d4d8",
  },
  divider: "#e4e4e7",
  success: {
    main: "#16a34a",
    light: "#22c55e",
    dark: "#15803d",
  },
  warning: {
    main: "#ea580c",
    light: "#fb923c",
    dark: "#c2410c",
  },
  error: {
    main: "#dc2626",
    light: "#ef4444",
    dark: "#b91c1c",
  },
  info: {
    main: "#0284c7",
    light: "#0ea5e9",
    dark: "#0369a1",
  },
};

const darkPalette = {
  primary: {
    main: "#ffffff",
    light: "#e4e4e7",
    dark: "#a1a1aa",
    contrastText: "#000000",
  },
  secondary: {
    main: "#a1a1aa",
    light: "#d4d4d8",
    dark: "#71717a",
    contrastText: "#000000",
  },
  background: {
    default: "#09090b",
    paper: "#18181b",
  },
  text: {
    primary: "#fafafa",
    secondary: "#a1a1aa",
    disabled: "#52525b",
  },
  divider: "#27272a",
  success: {
    main: "#22c55e",
    light: "#4ade80",
    dark: "#16a34a",
  },
  warning: {
    main: "#fb923c",
    light: "#fdba74",
    dark: "#ea580c",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  info: {
    main: "#0ea5e9",
    light: "#38bdf8",
    dark: "#0284c7",
  },
};

export const ThemeContextProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Load from cookie on initial render
    const savedMode = getCookie("theme-mode");
    return savedMode || "light";
  });

  // Save to cookie whenever mode changes
  useEffect(() => {
    setCookie("theme-mode", mode);
  }, [mode]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light" ? lightPalette : darkPalette),
        },
        typography: {
          fontFamily:
            '"Roboto", "-apple-system", "BlinkMacSystemFont", "Segoe UI", sans-serif',
          h1: {
            fontSize: "2.25rem",
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
          },
          h2: {
            fontSize: "1.875rem",
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
          },
          h3: {
            fontSize: "1.5rem",
            fontWeight: 700,
            lineHeight: 1.4,
          },
          h4: {
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: 1.4,
          },
          h5: {
            fontSize: "1rem",
            fontWeight: 600,
            lineHeight: 1.5,
          },
          h6: {
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1.5,
          },
          body1: {
            fontSize: "0.875rem",
            lineHeight: 1.6,
          },
          body2: {
            fontSize: "0.875rem",
            lineHeight: 1.6,
            color: mode === "light" ? "#71717a" : "#a1a1aa",
          },
          caption: {
            fontSize: "0.75rem",
            lineHeight: 1.5,
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
              },
              contained: {
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: "0.5rem",
                border: `1px solid ${mode === "light" ? "#e4e4e7" : "#27272a"}`,
                boxShadow:
                  mode === "light"
                    ? "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
                    : "0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: "0.5rem",
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.375rem",
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: "0.375rem",
              },
            },
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
